import { clsx, type ClassValue } from "clsx"
import type { GenerateStreamResponse, StreamChunk, Part } from "@/types/chat"
import { twMerge } from "tailwind-merge"
import { getAccessToken } from "./auth-bridge"
import { encodeTextForWafSafeTransport } from "./waf-safe-transport"

/** Runtime API base path, injected by demo-api at serve time */
export const API_BASE = (window as any).__CONTRACTACIO_CONFIG__?.apiBase ?? ''

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/**
 * Converts any timestamp format to a Date object.
 * Handles: Date, objects with .toDate(), serialized timestamps
 * ({ _seconds } or { seconds }), ISO strings, and unix milliseconds.
 */
export function parseTimestamp(timestamp: unknown): Date | null {
	if (!timestamp) return null
	if (timestamp instanceof Date) return timestamp
	if (typeof timestamp === "string") return new Date(timestamp)
	if (typeof timestamp === "number") return new Date(timestamp)
	if (typeof timestamp === "object" && timestamp !== null) {
		// Object with .toDate() method
		if ("toDate" in timestamp && typeof (timestamp as { toDate: () => Date }).toDate === "function") {
			return (timestamp as { toDate: () => Date }).toDate()
		}
		// Serialized timestamp: { _seconds, _nanoseconds }
		if ("_seconds" in timestamp) {
			return new Date((timestamp as { _seconds: number })._seconds * 1000)
		}
		// Serialized timestamp: { seconds, nanoseconds }
		if ("seconds" in timestamp) {
			return new Date((timestamp as { seconds: number }).seconds * 1000)
		}
	}
	return null
}

export function toReadableStream(
	response: GenerateStreamResponse,
	options?: {
		transform?: (chunk: StreamChunk) => unknown
		errorRef?: { current?: { message: string } }
	}
) {
	return new ReadableStream({
		async start(controller) {
			function enqueue(data: unknown) {
				const out = `data: ${JSON.stringify(data)}\n\n`
				controller.enqueue(out)
			}

			try {
				for await (const chunk of response.stream) {
					enqueue({
						message: options?.transform ? options.transform(chunk) : { ...chunk.toJSON(), output: chunk.output },
					})
				}

				const result = await response.response

				enqueue({
					result: {
						messages: [...result.messages],
						output: result.output,
					},
				})
			} catch (e) {
				console.error((e as Error).stack)
				enqueue({ error: { message: (e as Error).message } })
			} finally {
				setTimeout(() => {
					controller.close()
				}, 100)
			}
		},
	})
}

/**
 * Get an auth token for API calls.
 * Delegates to module-auth via auth-bridge for Bearer token acquisition.
 */
async function getApiToken(): Promise<string> {
	return getAccessToken()
}

/**
 * Build headers for authenticated API requests.
 */
function buildHeaders(token: string): Record<string, string> {
	return {
		"Content-Type": "application/json",
		"Authorization": `Bearer ${token}`,
	}
}

type JsonRequestOptions = {
	wafSafe?: boolean
}

export function toWafSafeJsonBody<T>(data: T, options?: JsonRequestOptions): string {
	return JSON.stringify(options?.wafSafe ? encodeTextForWafSafeTransport(data) : data)
}

/**
 * Get auth headers for manual fetch calls (e.g. SSE streams).
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
	const token = await getApiToken()
	return buildHeaders(token)
}

/**
 * fetch() that attaches the MSAL Bearer token. Use for any raw fetch to the API
 * (custom methods, multipart upload) that can't use the simpleGet/simplePost
 * helpers. On the chassis (onboarding/stage) environment auth is Bearer-only —
 * cookie `credentials: "include"` is NOT accepted, so cookie-only calls fail with
 * 401 TOKEN_INVALID.
 *
 * Adds Authorization; defaults Content-Type to JSON unless the body is FormData
 * (multipart upload must keep the browser-generated boundary) or the caller set
 * its own Content-Type. Callers pass the full `/api/...` path as before
 * (API_BASE is not prepended here).
 */
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
	const headers = new Headers(init.headers as HeadersInit | undefined)
	const token = await getApiToken()
	if (token) headers.set("Authorization", `Bearer ${token}`)
	if (!headers.has("Content-Type") && !(init.body instanceof FormData)) {
		headers.set("Content-Type", "application/json")
	}
	return fetch(input, { credentials: "include", ...init, headers })
}

/**
 * SWR-compatible fetcher with Bearer token auth.
 * Use as: useSWR(url, authFetcher)
 */
export const authFetcher = async <T = unknown>(url: string): Promise<T> => {
	const headers = await getAuthHeaders()
	const r = await fetch(API_BASE + url, { headers })
	if (!r.ok) throw new Error(`Fetch failed: ${r.status}`)
	return r.json() as Promise<T>
}

export async function simplePost<ReqData = unknown, ResultData = unknown>(path: string, data: ReqData, options?: JsonRequestOptions): Promise<ResultData> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "POST",
		headers: buildHeaders(token),
		body: toWafSafeJsonBody(data, options),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			throw new Error(error.error || error.message || "Request failed")
		}
		throw new Error(`Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`)
	}

	return await response.json()
}

export async function simpleGet<ResultData = unknown>(path: string): Promise<ResultData> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "GET",
		headers: buildHeaders(token),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			throw new Error(error.error || error.message || "Request failed")
		}
		throw new Error(`Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`)
	}

	return await response.json()
}

export async function simplePatch<ReqData = unknown, ResultData = unknown>(path: string, data: ReqData, options?: JsonRequestOptions): Promise<ResultData> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "PATCH",
		headers: buildHeaders(token),
		body: toWafSafeJsonBody(data, options),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			throw new Error(error.error || error.message || "Request failed")
		}
		throw new Error(`Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`)
	}

	return await response.json()
}

export async function simplePut<ReqData = unknown, ResultData = unknown>(path: string, data: ReqData, options?: JsonRequestOptions): Promise<ResultData> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "PUT",
		headers: buildHeaders(token),
		body: toWafSafeJsonBody(data, options),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			throw new Error(error.error || error.message || "Request failed")
		}
		throw new Error(`Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`)
	}

	return await response.json()
}

export async function simpleDelete<ResultData = unknown>(path: string): Promise<ResultData> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "DELETE",
		headers: buildHeaders(token),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			throw new Error(error.error || error.message || "Request failed")
		}
		throw new Error(`Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`)
	}

	return await response.json()
}

export function getPrivateBlobPath(source: string | null | undefined): string | null {
	if (!source) return null
	try {
		const url = new URL(source, window.location.origin)
		if (!url.hostname.endsWith(".blob.core.windows.net")) return null
		const [, container, ...blobParts] = url.pathname.split("/")
		if (!container || blobParts.length === 0) return null
		return decodeURIComponent(blobParts.join("/"))
	} catch {
		return null
	}
}

export function getAuthenticatedDownloadPath(source: string | null | undefined): string | null {
	if (!source) return null
	const privateBlobPath = getPrivateBlobPath(source)
	if (privateBlobPath) return `/api/files/download?path=${encodeURIComponent(privateBlobPath)}&inline=true`
	if (source.startsWith("/api/files/download")) return source
	return null
}

export function requiresAuthenticatedFileFetch(source: string | null | undefined): boolean {
	return Boolean(getAuthenticatedDownloadPath(source))
}

export async function fetchAuthenticatedFileBlob(source: string): Promise<Blob | null> {
	const downloadPath = getAuthenticatedDownloadPath(source)
	if (!downloadPath) return null

	const token = await getApiToken()
	const response = await fetch(API_BASE + downloadPath, {
		method: "GET",
		headers: { "Authorization": `Bearer ${token}` },
	})

	if (!response.ok) return null
	return response.blob()
}

export async function fetchAuthenticatedFileBytes(source: string): Promise<Uint8Array | null> {
	const blob = await fetchAuthenticatedFileBlob(source)
	if (!blob) return null
	return new Uint8Array(await blob.arrayBuffer())
}

export async function createAuthenticatedFileObjectUrl(source: string): Promise<string | null> {
	const blob = await fetchAuthenticatedFileBlob(source)
	if (!blob) return null
	return URL.createObjectURL(blob)
}

function getFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
	if (!contentDisposition) return undefined
	const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
	if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1])
	const asciiMatch = /filename="([^"]+)"/i.exec(contentDisposition)
	return asciiMatch?.[1]
}

export async function downloadAuthenticatedFile(
	path: string,
	options?: { openInNewTab?: boolean; fallbackFilename?: string }
): Promise<void> {
	const openedWindow = options?.openInNewTab ? window.open("about:blank", "_blank", "noopener,noreferrer") : null
	try {
		const token = await getApiToken()
		const response = await fetch(API_BASE + path, {
			method: "GET",
			headers: { "Authorization": `Bearer ${token}` },
		})

		if (!response.ok) {
			if (openedWindow) openedWindow.close()
			if (response.headers.get("content-type")?.includes("application/json")) {
				const error = await response.json()
				throw new Error(error.error || error.message || "Download failed")
			}
			throw new Error(`Download failed: ${response.status}`)
		}

		const blob = await response.blob()
		const objectUrl = URL.createObjectURL(blob)
		const filename = getFilenameFromContentDisposition(response.headers.get("content-disposition")) || options?.fallbackFilename || "download"

		if (options?.openInNewTab) {
			if (openedWindow) {
				openedWindow.location.href = objectUrl
			} else {
				window.open(objectUrl, "_blank")
			}
		} else {
			const link = document.createElement("a")
			link.href = objectUrl
			link.download = filename
			document.body.appendChild(link)
			link.click()
			link.remove()
		}

		window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
	} catch (error) {
		if (openedWindow && !openedWindow.closed) {
			openedWindow.document.body.textContent = error instanceof Error ? error.message : "Download failed"
		}
		throw error
	}
}

export async function* post<ReqData = unknown, ChunkData = unknown, ResultData = unknown>(
	path: string,
	data: ReqData,
	options?: JsonRequestOptions
): AsyncIterable<{
	message?: ChunkData
	result?: ResultData
	error?: { message: string; status?: number }
}> {
	const token = await getApiToken()

	const response = await fetch(API_BASE + path, {
		method: "POST",
		headers: buildHeaders(token),
		body: toWafSafeJsonBody(data, options),
	})

	if (!response.ok) {
		if (response.headers.get("content-type")?.includes("application/json")) {
			const error = await response.json()
			yield { error }
			return
		}
		yield {
			error: {
				message: `Unexpected HTTP error: ${(await response.text()).substring(0, 200)}`,
				status: response.status,
			},
		}
		return
	}

	const reader = response.body?.getReader()
	if (!reader) {
		throw new Error("No response body to read")
	}

	let buffer = ""
	while (true) {
		const { done, value } = await reader.read()
		if (done) {
			break
		}

		buffer += new TextDecoder().decode(value)
		const lines = buffer.split("\n\n")
		for (let i = 0; i < lines.length - 1; i++) {
			const line = lines[i].trim()
			if (line.startsWith("data: ")) {
				const jsonStr = line.substring("data: ".length)
				try {
					const chunk = JSON.parse(jsonStr)
					yield chunk // Assuming the actual data is under the "message" key
				} catch (error) {
					console.error("Failed to parse JSON chunk:", jsonStr, error)
				}
			}
		}
		buffer = lines[lines.length - 1]
	}
}

export function compactParts(parts: Part[]): Part[] {
	const out: Part[] = []
	for (const part of parts) {
		const lastPart = out.at(-1)
		if (lastPart?.text && part.text) lastPart.text += part.text
		else out.push(part)
	}
	return out
}
